import { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  ListChecks,
  Lock,
  Pause,
  Plus,
  Settings,
  Trash2,
  X,
  Zap,
} from 'lucide-react'
import {
  Board,
  BoardColumn,
  CardFieldKey,
  boardProfile,
  createColumn,
  deleteColumn,
  fieldDef,
  reorderColumns,
  stuckLabel,
  updateColumn,
} from '../../lib/boards'
import { inputClass, labelClass } from './CardFieldInput'
import { Switch, columnAccent, ghostButton, iconButton, overlayClass, panelClass, primaryButton } from './ui'

const KIND_META: Record<BoardColumn['kind'], { label: string; hint: string; icon?: typeof Zap }> = {
  stage: { label: 'Pipeline step', hint: 'Cards are moved here by hand' },
  auto: { label: 'Automatic', hint: 'Cards move here on their own with payments', icon: Zap },
  parked: { label: 'Side column', hint: 'Cards need a reason to enter', icon: Pause },
}

const chipClass = 'inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400'

// Columns, required fields and timings for one board, opened from the board itself.
// `columns` is this board's columns in order; changes go straight back to the board.
export function BoardSettingsPanel({
  board,
  columns,
  cardCounts,
  onColumnsChange,
  onColumnDeleted,
  onCardsStale,
  onReload,
  onClose,
}: {
  board: Board
  columns: BoardColumn[]
  // Cards per column as the board last loaded them, for the delete confirmation.
  cardCounts: Record<string, number>
  onColumnsChange: (update: (prev: BoardColumn[]) => BoardColumn[]) => void
  // The column and all its cards are gone.
  onColumnDeleted: (columnId: string) => void
  // The column held a different number of cards than the board showed: reload them.
  onCardsStale: () => void
  onReload: () => void
  onClose: () => void
}) {
  const [openId, setOpenId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (!message?.ok) return
    const t = setTimeout(() => setMessage(null), 4000)
    return () => clearTimeout(t)
  }, [message])

  const flash = (ok: boolean, text: string) => setMessage({ ok, text })

  const replaceColumn = (col: BoardColumn) => onColumnsChange(prev => prev.map(c => (c.id === col.id ? col : c)))

  const move = async (col: BoardColumn, direction: -1 | 1) => {
    const changes = reorderColumns(columns, col.id, direction)
    if (changes.length === 0) return
    setBusy(true)
    try {
      const updated = await Promise.all(changes.map(ch => updateColumn(ch.id, { sortOrder: ch.sortOrder })))
      updated.forEach(replaceColumn)
    } catch (e: any) {
      flash(false, e?.message || 'Could not reorder.')
      onReload()
    } finally {
      setBusy(false)
    }
  }

  const addColumn = async () => {
    // Enter isn't blocked like the button, so a second Enter mid-add would add the column twice.
    if (busy || !newName.trim()) return
    setBusy(true)
    try {
      const firstNonStage = columns.find(c => c.kind !== 'stage')
      const at = firstNonStage ? firstNonStage.sortOrder : columns.length + 1
      const shifted = await Promise.all(
        columns.filter(c => c.sortOrder >= at).map(c => updateColumn(c.id, { sortOrder: c.sortOrder + 1 }))
      )
      shifted.forEach(replaceColumn)
      const created = await createColumn(board.id, newName, at)
      onColumnsChange(prev => [...prev, created])
      setNewName('')
      setOpenId(created.id)
      flash(true, `Added "${created.name}".`)
    } catch (e: any) {
      flash(false, e?.message || 'Could not add the column.')
      onReload()
    } finally {
      setBusy(false)
    }
  }

  // The row asks first, on the page: browser pop-ups (window.confirm) can be blocked, which
  // silently cancelled every delete.
  const removeColumn = async (col: BoardColumn) => {
    setBusy(true)
    try {
      const cards = await deleteColumn(col.id, cardCounts[col.id] ?? 0)
      onColumnsChange(prev => prev.filter(c => c.id !== col.id))
      onColumnDeleted(col.id)
      flash(true, cards > 0 ? `Deleted "${col.name}" and its ${cards === 1 ? 'card' : `${cards} cards`}.` : `Deleted "${col.name}".`)
    } catch (e: any) {
      if (e?.cardCountChanged) onCardsStale()
      flash(false, e?.message || 'Could not delete the column.')
    } finally {
      setBusy(false)
    }
  }

  // Only the close button closes it: a click outside would throw away a column's unsaved edits.
  return (
    <div className={`${overlayClass} z-50`}>
      <div
        className={`${panelClass} w-full max-w-2xl max-h-[90vh] flex flex-col`}
        role="dialog"
        aria-modal="true"
        aria-label="Board settings"
      >
        <div className="flex items-start justify-between gap-4 p-6 pb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-brand-400/10 text-brand-600 dark:text-brand-400 flex items-center justify-center">
              <Settings className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{board.name} board settings</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Columns, required fields and timings. Only super admins can change these.</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className={iconButton}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {message && (
          <div
            role="status"
            className={`mx-6 mb-4 flex items-start gap-3 p-3 pr-2 rounded-xl border text-sm ${
              message.ok
                ? 'border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-300'
                : 'border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-300'
            }`}
          >
            {message.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            <span className="flex-1">{message.text}</span>
            <button onClick={() => setMessage(null)} aria-label="Dismiss" className="p-0.5 rounded opacity-70 hover:opacity-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-6 py-3 border-y border-gray-100 dark:border-white/[0.06]">
          <p className="text-xs text-gray-500 dark:text-gray-400">In board order, left to right. Click a column to edit it.</p>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">{columns.length} columns</span>
        </div>

        <ol className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100 dark:divide-white/[0.06]">
          {columns.map((col, i) => (
            <ColumnRow
              key={col.id}
              index={i + 1}
              boardKey={board.key}
              column={col}
              open={openId === col.id}
              first={i === 0}
              last={i === columns.length - 1}
              busy={busy}
              cardCount={cardCounts[col.id] ?? 0}
              onToggle={() => setOpenId(openId === col.id ? null : col.id)}
              onMove={(d) => move(col, d)}
              onDelete={() => removeColumn(col)}
              onSaved={(c) => {
                replaceColumn(c)
                flash(true, `Saved "${c.name}".`)
              }}
              onError={(text) => flash(false, text)}
            />
          ))}
        </ol>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.02] rounded-b-2xl">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Plus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addColumn()}
                placeholder="Add a pipeline step, e.g. Trial lesson booked"
                aria-label="New pipeline step"
                className={`${inputClass} pl-9`}
              />
            </div>
            <button onClick={addColumn} disabled={busy || !newName.trim()} className={`${primaryButton} whitespace-nowrap`}>
              Add step
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">New steps are added before Active. Move them with the arrows.</p>
        </div>
      </div>
    </div>
  )
}

function ColumnRow({
  index,
  boardKey,
  column,
  open,
  first,
  last,
  busy,
  cardCount,
  onToggle,
  onMove,
  onDelete,
  onSaved,
  onError,
}: {
  index: number
  boardKey: string
  column: BoardColumn
  open: boolean
  first: boolean
  last: boolean
  busy: boolean
  cardCount: number
  onToggle: () => void
  onMove: (direction: -1 | 1) => void
  onDelete: () => void
  onSaved: (col: BoardColumn) => void
  onError: (text: string) => void
}) {
  const initialDays = column.stuckAfterHours != null ? String(Math.max(column.stuckAfterHours / 24, 1)) : '3'
  const [name, setName] = useState(column.name)
  const [helpText, setHelpText] = useState(column.helpText ?? '')
  const [stuckOn, setStuckOn] = useState(column.stuckAfterHours != null)
  const [stuckDays, setStuckDays] = useState(initialDays)
  const [required, setRequired] = useState<CardFieldKey[]>(column.requiredFields)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Resets only when the saved values change (a save, or another admin's edit after a reload), not
  // when the column is moved or reloaded unchanged, so unsaved edits survive those.
  const { name: savedName, helpText: savedHelpText, stuckAfterHours: savedStuckHours } = column
  const savedRequired = column.requiredFields.join(',')
  const reset = useCallback(() => {
    setName(savedName)
    setHelpText(savedHelpText ?? '')
    setStuckOn(savedStuckHours != null)
    setStuckDays(savedStuckHours != null ? String(Math.max(savedStuckHours / 24, 1)) : '3')
    setRequired(savedRequired ? (savedRequired.split(',') as CardFieldKey[]) : [])
  }, [savedName, savedHelpText, savedStuckHours, savedRequired])

  useEffect(reset, [reset])

  const parked = column.kind === 'parked'
  const dirty =
    name !== column.name ||
    helpText !== (column.helpText ?? '') ||
    stuckOn !== (column.stuckAfterHours != null) ||
    (stuckOn && stuckDays !== initialDays) ||
    (!parked && (required.length !== column.requiredFields.length || required.some(k => !column.requiredFields.includes(k))))

  const toggleField = (key: CardFieldKey) =>
    setRequired(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))

  const save = async () => {
    if (!name.trim()) return onError('The column needs a name.')
    const days = parseFloat(stuckDays)
    if (stuckOn && (!isFinite(days) || days <= 0)) return onError('Stuck time must be more than 0 days.')
    setSaving(true)
    try {
      const updated = await updateColumn(column.id, {
        name,
        helpText,
        stuckAfterHours: stuckOn ? Math.round(days * 24) : null,
        requiredFields: parked ? column.requiredFields : required,
      })
      onSaved(updated)
    } catch (e: any) {
      onError(e?.message || 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  const accent = columnAccent(column)
  const kind = KIND_META[column.kind]
  const KindIcon = kind.icon

  return (
    <li className={open ? 'bg-gray-50/80 dark:bg-white/[0.03]' : ''}>
      <div className={`flex items-center gap-3 px-6 py-3 transition-colors ${open ? '' : 'hover:bg-gray-50/60 dark:hover:bg-white/[0.02]'}`}>
        <span className="w-4 text-right text-xs font-medium text-gray-400 dark:text-gray-500 tabular-nums">{index}</span>
        <button onClick={onToggle} aria-expanded={open} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${accent.dot}`} />
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{column.name}</span>
              {column.isSystem && (
                <span title="Built-in column: it can be edited but not deleted">
                  <Lock className="h-3.5 w-3.5 text-gray-400" />
                </span>
              )}
            </span>
            <span className="flex flex-wrap items-center gap-1.5 mt-1">
              <span className={chipClass} title={kind.hint}>
                {KindIcon && <KindIcon className="h-3 w-3" />} {kind.label}
              </span>
              <span className={chipClass}>
                <Clock className="h-3 w-3" />
                {column.stuckAfterHours == null ? 'Never stuck' : `Stuck after ${stuckLabel(column.stuckAfterHours)}`}
              </span>
              {column.requiredFields.length > 0 && (
                <span className={chipClass}>
                  <ListChecks className="h-3 w-3" /> {column.requiredFields.length} required to leave
                </span>
              )}
              {column.entryFields.length > 0 && <span className={chipClass}>Reason required</span>}
            </span>
          </span>
          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        <div className="flex items-center gap-0.5">
          <button className={iconButton} disabled={busy || first} onClick={() => onMove(-1)} title="Move up" aria-label="Move up">
            <ArrowUp className="h-4 w-4" />
          </button>
          <button className={iconButton} disabled={busy || last} onClick={() => onMove(1)} title="Move down" aria-label="Move down">
            <ArrowDown className="h-4 w-4" />
          </button>
          {!column.isSystem && (
            <button
              className={`${iconButton} hover:!text-red-500 hover:!bg-red-500/10`}
              disabled={busy || confirmDelete}
              onClick={() => setConfirmDelete(true)}
              title="Delete column"
              aria-label="Delete column"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="flex flex-wrap items-center gap-2 px-6 pb-3 text-sm">
          <span className="sm:ml-[3.25rem] flex-1 min-w-[12rem] text-red-500">
            {cardCount > 0
              ? `Delete "${column.name}" and the ${cardCount === 1 ? 'card' : `${cardCount} cards`} in it? Their notes, tasks, log, comments and history go too. This can't be undone.`
              : `Delete "${column.name}"? It has no cards.`}
          </span>
          <button
            onClick={() => {
              setConfirmDelete(false)
              onDelete()
            }}
            disabled={busy}
            className="h-8 px-3 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 disabled:opacity-40 transition-colors"
          >
            {cardCount > 0 ? `Delete column and ${cardCount === 1 ? 'card' : `${cardCount} cards`}` : 'Delete'}
          </button>
          <button
            onClick={() => setConfirmDelete(false)}
            className="h-8 px-3 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
          >
            Keep
          </button>
        </div>
      )}

      {open && (
        <div className="px-6 pb-5">
          <div className="sm:ml-[3.25rem] rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-surface-100 p-4 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Mark as stuck</label>
                <div className="flex items-center gap-2.5 h-[38px]">
                  <Switch checked={stuckOn} onChange={setStuckOn} label="Mark as stuck" />
                  {stuckOn ? (
                    <>
                      <span className="text-sm text-gray-700 dark:text-gray-300">after</span>
                      <input
                        type="number"
                        min="0.5"
                        step="0.5"
                        value={stuckDays}
                        onChange={(e) => setStuckDays(e.target.value)}
                        aria-label="Days"
                        className={`${inputClass} w-20`}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">days</span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">Never</span>
                  )}
                </div>
                {column.key === 'paused' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Also sets when the "Check in with the family" task is due.</p>
                )}
              </div>
            </div>

            <div>
              <label className={labelClass}>Help text</label>
              <input value={helpText} onChange={(e) => setHelpText(e.target.value)} placeholder="Shown on the card" className={inputClass} />
            </div>

            {parked ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Cards need a{' '}
                <span className="font-medium">{column.entryFields.map(f => fieldDef(f, boardKey).label.toLowerCase()).join(', ') || 'reason'}</span> to
                enter this column.
              </p>
            ) : (
              <div>
                <label className={labelClass}>Must be filled in before a card can leave this column</label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {boardProfile(boardKey).requirableFields.map(key => {
                    const on = required.includes(key)
                    return (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleField(key)}
                        className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-xs font-medium transition-colors ${
                          on
                            ? 'border-brand-400/40 bg-brand-400/10 text-brand-600 dark:text-brand-400'
                            : 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
                        }`}
                      >
                        {on && <Check className="h-3 w-3" />}
                        {fieldDef(key, boardKey).label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              {dirty && (
                <button onClick={reset} className={ghostButton}>
                  Reset
                </button>
              )}
              <button onClick={save} disabled={saving || !dirty} className={primaryButton}>
                <Check className="h-4 w-4" /> {saving ? 'Saving…' : 'Save column'}
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  )
}
